import { ProblemResolutionStopOutcome } from "@redotech/crdb-enums/fulfillment/problem-resolution-stop-outcome";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import {
  inventoryLevelIdFormat,
  problemResolutionIdFormat,
  storageLocationIdFormat,
} from "@redotech/redo-warehouse-id";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

const ProblemResolutionSchema = z.object({
  id: problemResolutionIdFormat.schema,
  locationId: locationIdFormat.schema,
  toteName: z.string().nullable(),
  createdAt: z.string(),
});

export const ProblemResolutionStopSnapshotSchema = z.object({
  productId: productIdFormat.schema.nullable(),
  productTitle: z.string(),
  variantTitle: z.string(),
  sku: z.string().nullable(),
  imageUrl: z.string().nullable(),
  neededQuantity: z.number(),
  orders: z.array(
    z.object({
      fulfillmentGroupId: zExt.objectId(),
      orderName: z.string(),
      quantity: z.number(),
    }),
  ),
  storageLocationId: storageLocationIdFormat.schema.nullable(),
  binName: z.string().nullable(),
  binType: z.string().nullable(),
  lastCountedAt: z.coerce.date().nullable(),
  availableQuantity: z.number().nullable(),
  onHandQuantity: z.number().nullable(),
  lotTracked: z.boolean(),
});

const ProblemResolutionStopSchema = z.object({
  stopKey: z.string(),
  outcome: z.nativeEnum(ProblemResolutionStopOutcome),
  pickedQuantity: z.number(),
  countedQuantity: z.number().nullable(),
  stop: ProblemResolutionStopSnapshotSchema,
});

export const startProblemResolution = {
  name: "startProblemResolution" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    toteBarcode: z.string().nullish(),
    fulfillmentGroupIds: z.array(zExt.objectId()),
  }),
  output: z.object({ problemResolution: ProblemResolutionSchema }),
};

export const getActiveProblemResolution = {
  name: "getActiveProblemResolution" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({
    problemResolution: ProblemResolutionSchema.nullable(),
    fulfillmentGroupIds: z.array(zExt.objectId()),
    holdings: z.array(
      z.object({
        fulfillmentGroupId: zExt.objectId(),
        orderName: z.string(),
        productId: productIdFormat.schema,
        quantity: z.number(),
        orderToteNames: z.array(z.string()),
        orderToteBarcodes: z.array(z.string()),
        productTitle: z.string().nullable(),
        variantTitle: z.string().nullable(),
        sku: z.string().nullable(),
        imageUrl: z.string().nullable(),
      }),
    ),
    stops: z.array(ProblemResolutionStopSchema),
    picks: z.array(
      z.object({
        fulfillmentGroupId: zExt.objectId(),
        productId: productIdFormat.schema,
        quantity: z.number(),
      }),
    ),
  }),
};

export const pickIntoProblemResolution = {
  name: "pickIntoProblemResolution" as const,
  input: z.object({
    problemResolutionId: problemResolutionIdFormat.schema,
    fulfillmentGroupId: zExt.objectId(),
    sources: z
      .array(
        z.object({
          inventoryLevelId: inventoryLevelIdFormat.schema,
          productId: productIdFormat.schema,
          quantity: z.number().int().min(1),
        }),
      )
      .min(1),
  }),
  output: z.object({ success: z.boolean(), message: z.string().nullish() }),
};

export const recordProblemResolutionStop = {
  name: "recordProblemResolutionStop" as const,
  input: z.object({
    problemResolutionId: problemResolutionIdFormat.schema,
    stopKey: z.string(),
    outcome: z.nativeEnum(ProblemResolutionStopOutcome),
    pickedQuantity: z.number().int().min(0),
    countedQuantity: z.number().int().min(0).nullable(),
    stop: ProblemResolutionStopSnapshotSchema,
  }),
  output: z.object({ success: z.boolean() }),
};

export const undoProblemResolutionStop = {
  name: "undoProblemResolutionStop" as const,
  input: z.object({
    problemResolutionId: problemResolutionIdFormat.schema,
    stopKey: z.string(),
  }),
  output: z.object({ success: z.boolean() }),
};

export const listOpenProblemResolutions = {
  name: "listOpenProblemResolutions" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({
    resolutions: z.array(
      z.object({
        id: problemResolutionIdFormat.schema,
        userName: z.string(),
        mine: z.boolean(),
        toteName: z.string().nullable(),
        createdAt: z.string(),
        lastActivityAt: z.string(),
        orderCount: z.number(),
        pickedUnits: z.number(),
      }),
    ),
  }),
};

export const takeOverProblemResolution = {
  name: "takeOverProblemResolution" as const,
  input: z.object({ problemResolutionId: problemResolutionIdFormat.schema }),
  output: z.object({ success: z.boolean(), message: z.string().nullish() }),
};

export const cancelProblemResolution = {
  name: "cancelProblemResolution" as const,
  input: z.object({ problemResolutionId: problemResolutionIdFormat.schema }),
  output: z.object({ success: z.boolean(), message: z.string().nullish() }),
};

export const finishProblemResolution = {
  name: "finishProblemResolution" as const,
  input: z.object({ problemResolutionId: problemResolutionIdFormat.schema }),
  output: z.object({
    success: z.boolean(),
    partialOrders: z
      .array(
        z.object({
          fulfillmentGroupId: zExt.objectId(),
          orderName: z.string(),
          heldQuantity: z.number(),
        }),
      )
      .optional(),
  }),
};

export const changeProblemResolutionTote = {
  name: "changeProblemResolutionTote" as const,
  input: z.object({
    problemResolutionId: problemResolutionIdFormat.schema,
    toteBarcode: z.string().min(1),
  }),
  output: z.object({ success: z.boolean(), message: z.string().nullish() }),
};

export const returnProblemResolutionOrderUnits = {
  name: "returnProblemResolutionOrderUnits" as const,
  input: z.object({
    problemResolutionId: problemResolutionIdFormat.schema,
    fulfillmentGroupId: zExt.objectId(),
    binName: z.string().min(1),
    productId: productIdFormat.schema.nullish(),
    quantity: z.number().int().min(1).nullish(),
  }),
  output: z.object({
    success: z.boolean(),
    message: z.string().nullish(),
    returnedQuantity: z.number().optional(),
  }),
};

export const getProblemResolutionClaims = {
  name: "getProblemResolutionClaims" as const,
  input: z.object({ fulfillmentGroupIds: z.array(zExt.objectId()) }),
  output: z.object({
    claims: z.array(
      z.object({
        fulfillmentGroupId: zExt.objectId(),
        userName: z.string(),
        mine: z.boolean(),
        pickedUnits: z.number(),
        picks: z.array(
          z.object({ productId: productIdFormat.schema, quantity: z.number() }),
        ),
      }),
    ),
  }),
};

export const transferProblemResolutionOrder = {
  name: "transferProblemResolutionOrder" as const,
  input: z.object({
    problemResolutionId: problemResolutionIdFormat.schema,
    fulfillmentGroupId: zExt.objectId(),
    toteBarcode: z.string(),
  }),
  output: z.object({ success: z.boolean(), message: z.string().nullish() }),
};
